import { Avatar } from "@coucou/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { MesDocuments } from "./components/documents";
import { Admin, ClientsPanel } from "./routes/admin";
import { GiftCards } from "./routes/gift-cards";
import { Inbox } from "./routes/inbox";
import { Partners } from "./routes/partners";
import { Planning } from "./routes/planning";
import { Pulse } from "./routes/pulse";
import { SignIn } from "./routes/sign-in";
import { clearSession, loadSession, type StoredSession } from "./lib/session";
import { api } from "./lib/trpc";

type View =
  | "pulse"
  | "planning"
  | "inbox"
  | "clients"
  | "giftCards"
  | "partners"
  | "documents"
  | "admin";

const NAV: { key: View; label: string; glyph: string; managerOnly?: boolean }[] = [
  { key: "pulse", label: "Aujourd'hui", glyph: "☕" },
  { key: "planning", label: "Planning", glyph: "🗓️" },
  { key: "inbox", label: "Messages", glyph: "💬" },
  // Les fiches clientes se consultent plusieurs fois par jour — au telephone,
  // au comptoir, avant une pose. Les laisser au fond des reglages obligeait a
  // trois clics pour retrouver une allergie.
  { key: "clients", label: "Clientes", glyph: "👩" },
  /*
   * Ouvert a toute l'equipe : c'est au comptoir qu'on vend une carte et qu'on
   * retrouve celle d'une cliente. Les corrections de solde et les types de
   * cartes restent reserves a la gerante, cote serveur comme a l'ecran.
   */
  { key: "giftCards", label: "Cartes cadeaux", glyph: "🎁" },
  // Ecoles, CFA, candidatures : des noms et des appreciations, pas un ecran
  // de cabine. Masque aux employees plutot que de leur ouvrir un refus.
  { key: "partners", label: "Partenariats", glyph: "🤝", managerOnly: true },
  /*
   * Accessible a TOUTE l'equipe, pas seulement aux gerantes.
   *
   * C'est le principe de cet ecran : une employee doit pouvoir retrouver son
   * contrat ou sa derniere fiche de paie sans demander a personne. Le range
   * dans les reglages l'aurait rendu invisible a celles a qui il est destine.
   */
  { key: "documents", label: "Mes documents", glyph: "📄" },
  // Range en dernier, volontairement : les reglages s'ouvrent une fois par
  // mois, le planning cinquante fois par jour.
  { key: "admin", label: "Réglages", glyph: "⚙️" },
];

/**
 * Coquille de l'application desktop.
 *
 * Trois destinations, pas une de plus. Un poste de salon n'est pas un
 * back-office : tout ce qui n'est pas quotidien (catalogue, equipe, tarifs)
 * vivra dans un ecran « Reglages » separe, volontairement moins accessible.
 */
export function App() {
  const [session, setSession] = useState<StoredSession | null>(() => loadSession());
  const [view, setView] = useState<View>("pulse");
  const queryClient = useQueryClient();

  if (!session) return <SignIn onSignedIn={setSession} />;

  const isManager = session.user.role === "owner" || session.user.role === "manager";
  const nav = NAV.filter((item) => !item.managerOnly || isManager);

  const signOut = async () => {
    // On ferme la session cote serveur AVANT de vider le local : sinon le
    // jeton resterait valide 12 h sur un poste partage.
    await api.auth.signOut.mutate().catch(() => undefined);
    clearSession();
    queryClient.clear();
    setSession(null);
  };

  return (
    <div className="flex h-dvh overflow-hidden bg-creme-100">
      <nav className="flex w-52 shrink-0 flex-col border-r border-creme-200 bg-creme-50 p-3">
        <p className="px-2 py-3 font-display text-lg text-blush-600">Coucou Nails</p>

        <ul className="flex-1 space-y-1">
          {nav.map((item) => (
            <li key={item.key}>
              <button
                type="button"
                onClick={() => setView(item.key)}
                aria-current={view === item.key ? "page" : undefined}
                className={[
                  "flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 text-left text-[15px]",
                  "transition-colors duration-[120ms]",
                  view === item.key
                    ? "bg-white font-medium text-encre-900 shadow-soft"
                    : "text-encre-600 hover:bg-creme-200",
                ].join(" ")}
              >
                <span aria-hidden>{item.glyph}</span>
                {item.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 rounded-lg p-2">
          <Avatar firstName={session.user.firstName} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-encre-800">
              {session.user.firstName}
            </p>
            <button
              type="button"
              onClick={signOut}
              className="text-xs text-encre-400 transition-colors hover:text-grenat-700"
            >
              Se déconnecter
            </button>
          </div>
        </div>
      </nav>

      <main className="min-w-0 flex-1">
        {view === "pulse" && <Pulse onOpenInbox={() => setView("inbox")} />}
        {view === "planning" && <Planning />}
        {view === "inbox" && <Inbox />}
        {view === "clients" && (
          <div className="h-full min-h-0 overflow-auto px-6 py-6">
            <ClientsPanel />
          </div>
        )}
        {view === "giftCards" && <GiftCards isManager={isManager} />}
        {view === "partners" && isManager && <Partners />}
        {view === "documents" && <MesDocuments />}
        {view === "admin" && <Admin />}
      </main>
    </div>
  );
}
